import type { IConditionalVariable, IConditionalVariableValue, ConditionalVariableType } from '@models/Forms/IForms';
import { substituteFormulaData } from './formulaStringLiteral';

type FormulaEvaluator = (expression: string, variables: Record<string, unknown>) => Promise<unknown>;
interface EvaluationOptions {
    allowShadowInitialValues?: boolean;
    declaredNames?: ReadonlySet<string>;
    prepareExpression?: (expression: string) => string;
    alreadyResolvedNames?: ReadonlySet<string>;
    /** Уже вычисленные переменные с объявленным типом string (глобальные для локальных). */
    stringNames?: ReadonlySet<string>;
}

const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);
const getPathSegments = (path: string) =>
    path
        .trim()
        .replace(/^['"]|['"]$/g, '')
        .split('.')
        .map((segment) =>
            segment
                .trim()
                .replace(/^\[|\]$/g, '')
                .replace(/^['"]|['"]$/g, ''),
        );

export const getVariableReferences = (text: string): string[] => {
    const references: string[] = [];
    const regex = /\{variables\(([^()]*)\)\}|variables\(([^()]*)\)/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
        const root = getPathSegments(match[1] ?? match[2])[0];
        if (root) references.push(root);
    }
    return references;
};

const VARIABLE_REFERENCE = /\{variables\(([^()]*)\)\}|variables\(([^()]*)\)/;

/** Значение ссылки; `undefined`, если её нет или она пуста (в strict — исключение). */
const lookupVariable = (rawPath: string, variables: Record<string, unknown>, strict: boolean): unknown => {
    const path = rawPath.trim().replace(/^['"]|['"]$/g, '');
    let value: any = variables;
    for (const segment of getPathSegments(rawPath)) {
        if (value == null || !hasOwn(Object(value), segment)) {
            if (strict) throw new Error(`Unknown variable reference "${path}".`);
            return undefined;
        }
        value = value[segment];
    }
    if (value === null || value === undefined) {
        if (strict) throw new Error(`Variable reference "${path}" has no value.`);
        return undefined;
    }
    return value;
};

/** Значение одной ссылки `variables(...)` / `{variables(...)}` (strict). */
export const resolveVariableReference = (token: string, variables: Record<string, unknown>): unknown => {
    const match = new RegExp(`^(?:${VARIABLE_REFERENCE.source})$`).exec(token);
    if (!match) throw new Error(`Invalid variable reference "${token}".`);
    return lookupVariable(match[1] ?? match[2], variables, true);
};

export const resolveVariableReferences = (text: string, variables: Record<string, unknown>, strict = false): string =>
    text.replace(new RegExp(VARIABLE_REFERENCE.source, 'g'), (_token, wrappedPath: string, plainPath: string) => {
        const value = lookupVariable(wrappedPath ?? plainPath, variables, strict);
        return value === undefined ? '' : String(value);
    });

/** То же для вычисляемого выражения: значение встаёт литералом, а не кодом. Переменная из
 *  `stringNames` (объявленный тип string) — всегда строковый литерал: `'007'` не становится `7`. */
export const resolveFormulaVariableReferences = (
    expression: string,
    variables: Record<string, unknown>,
    strict = false,
    stringNames?: ReadonlySet<string>,
): string =>
    substituteFormulaData(expression, VARIABLE_REFERENCE, (_token, [wrappedPath, plainPath]) => {
        const path = (wrappedPath ?? plainPath)!;
        const value = lookupVariable(path, variables, strict);
        if (value === undefined) return { raw: '' };
        return typeof value === 'string' && stringNames?.has(getPathSegments(path)[0]) ? { text: value } : { value };
    });

const validateTypedValue = (name: string, type: ConditionalVariableType, value: unknown, area: string) => {
    const valid =
        type === 'string'
            ? typeof value === 'string'
            : type === 'number'
              ? typeof value === 'number' && Number.isFinite(value)
              : type === 'boolean' && typeof value === 'boolean';
    if (!valid) throw new Error(`${area} variable "${name}" must resolve to ${type}.`);
    return value;
};

const evaluateValue = async (
    name: string,
    type: ConditionalVariableType,
    branch: IConditionalVariableValue,
    area: string,
    evaluate: (expression: string) => Promise<unknown>,
) => {
    const hasValue = hasOwn(branch, 'value');
    let value: unknown;
    if (hasValue) {
        const literal = branch.value!;
        if (type === 'string') value = literal;
        else if (type === 'boolean') {
            if (literal !== 'true' && literal !== 'false')
                throw new Error(`${area} variable "${name}" has invalid boolean literal.`);
            value = literal === 'true';
        } else {
            if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(literal))
                throw new Error(`${area} variable "${name}" has invalid number literal.`);
            value = Number(literal);
        }
    } else value = await evaluate(branch.expr!);
    return validateTypedValue(name, type, value, area);
};

export const evaluateConditionalVariables = async (
    definitions: IConditionalVariable[] | undefined,
    initialValues: Record<string, unknown>,
    area: string,
    evaluate: FormulaEvaluator,
    options: EvaluationOptions = {},
) => {
    const {
        allowShadowInitialValues = false,
        declaredNames,
        alreadyResolvedNames,
        prepareExpression = (expression: string) => expression,
    } = options;
    const stringNames = new Set(options.stringNames);
    const values: Record<string, unknown> = Object.assign(Object.create(null), initialValues);
    const names = new Set<string>();
    const resolvedNames = new Set(alreadyResolvedNames);
    for (const variable of definitions ?? []) {
        if (
            typeof variable?.name !== 'string' ||
            !variable.name.trim() ||
            names.has(variable.name) ||
            (!allowShadowInitialValues && hasOwn(initialValues, variable.name))
        ) {
            throw new Error(`${area} has an invalid or duplicate variable name "${variable?.name ?? ''}".`);
        }
        if (!['string', 'number', 'boolean'].includes(variable.type)) {
            throw new Error(`${area} variable "${variable.name}" has an unsupported type.`);
        }
        names.add(variable.name);
        if (variable.type === 'string') stringNames.add(variable.name);
        else stringNames.delete(variable.name);
    }

    for (const variable of definitions ?? []) {
        if (!variable.if) throw new Error(`${area} variable "${variable.name}" must define if.`);
        const branches = [variable.if, ...(variable.elseif ?? [])];
        for (const branch of branches) {
            if (typeof branch?.condition !== 'string' || !branch.condition.trim()) {
                throw new Error(`${area} variable "${variable.name}" has an invalid branch condition.`);
            }
        }
        const allValueBranches = [...branches, ...(variable.else ? [variable.else] : [])];
        for (const branch of allValueBranches) {
            const hasValue = hasOwn(branch, 'value');
            const hasExpr = hasOwn(branch, 'expr');
            if (
                hasValue === hasExpr ||
                (hasValue && typeof branch.value !== 'string') ||
                (hasExpr && (typeof branch.expr !== 'string' || !branch.expr.trim()))
            ) {
                throw new Error(`${area} variable "${variable.name}" must have exactly one of value or expr.`);
            }
            const source = `${'condition' in branch ? branch.condition : ''} ${branch.expr ?? ''}`;
            for (const reference of getVariableReferences(source)) {
                if (reference === variable.name)
                    throw new Error(`${area} variable "${variable.name}" cannot reference itself.`);
                if ((names.has(reference) || declaredNames?.has(reference)) && !resolvedNames.has(reference))
                    throw new Error(`${area} variable "${variable.name}" references future variable "${reference}".`);
                if (!hasOwn(values, reference))
                    throw new Error(`${area} variable "${variable.name}" references unknown variable "${reference}".`);
            }
        }
        const evaluateExpression = async (expression: string) => {
            try {
                return await evaluate(
                    resolveFormulaVariableReferences(prepareExpression(expression), values, true, stringNames),
                    values,
                );
            } catch (error) {
                throw new Error(
                    `${area} variable "${variable.name}" could not evaluate expression: ${error instanceof Error ? error.message : String(error)}`,
                    { cause: error },
                );
            }
        };
        let selected: IConditionalVariableValue | undefined;
        for (const branch of branches) {
            const condition = await evaluateExpression(branch.condition);
            if (condition === undefined)
                throw new Error(`${area} variable "${variable.name}" could not evaluate branch condition.`);
            if (condition) {
                selected = branch;
                break;
            }
        }
        selected ??= variable.else;
        if (!selected) throw new Error(`${area} variable "${variable.name}" has no matching branch and no else.`);
        values[variable.name] = await evaluateValue(variable.name, variable.type, selected, area, evaluateExpression);
        resolvedNames.add(variable.name);
    }
    return values;
};
